import { useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useModal } from "react-modal-hook";
import { RESOURCE_NAME } from "common/authorization";
import {
  DOCUMENT_UPLOAD_DETAIL_TAB_NAME,
  MIME_TYPE,
  OBJECT_TYPE,
  SUBMISSION_TYPE_MODULE,
} from "common/constants";
import { buildLink } from "common/routing";
import {
  CertificateUploadStatus,
  GetDocumentUploadsQuery,
} from "../../generated/graphql";
import { useStatusToasts } from "../../hooks/useStatusToasts";
import { getDocumentUploadUrl } from "../../utils/documentUploads";
import { createTracker, track } from "../../utils/tracking";
import { AuthContext } from "../Authorization/AuthContext";
import { Actions } from "../Common/ActionCell";
import { Button } from "../Common/Buttons/Button";
import Modal from "../Common/Modal";
import { useSendMailModal } from "../Common/SendMailModal/SendMailModal";
import {
  ActionsProps,
  shouldShowViewOnMapActionForCustomMapGeometry,
  shouldShowViewOnMapActionForProperty,
} from "../Inputs/DropdownMenu";
import { useIssueModal } from "../Issues";
import { DeleteDocumentUploadConfirmation } from "./DeleteConfirmation";
import { useShareFileModal } from "./ElevationCertificates/ShareFileModal";

export interface ActionButtonProps {
  isDetailView?: boolean;
  disabled?: boolean;
  httpClient?: typeof fetch;
  onUpdate?: () => void;
  documentUpload: Omit<
    GetDocumentUploadsQuery["documentUploads"]["data"][number],
    "url" | "name" | "property" | "elevationCertificate"
  > & {
    property?: Maybe<{
      id: string;
      streetAddress?: Maybe<string>;
      longitude: number;
      latitude: number;
    }>;
    customMapGeometry?: Maybe<{
      id: string;
      customMapId?: string;
    }>;
    elevationCertificate?: Maybe<{
      id: string;
      issuedAt?: any | null;
      certfierInfo?: {
        name: Maybe<string>;
        email: Maybe<string>;
      };
    }>;
    certificateUpload?: Maybe<{
      status: Maybe<CertificateUploadStatus>;
    }>;
    isDeletable?: boolean;
  };
}

const ActionButton = ({
  documentUpload,
  isDetailView = false,
  disabled = false,
  httpClient = fetch,
  onUpdate,
}: ActionButtonProps) => {
  const {
    property,
    customMapGeometry,
    elevationCertificate,
    accountDocumentType,
    id: documentUploadId,
    originalFilename,
    formData,
    isDeletable,
    hiddenFromPublic,
    certificateUpload,
    mimeType,
  } = documentUpload;

  const coordinates = [property?.longitude!, property?.latitude!] as const;

  const [loadingDocxExport, setLoadingDocxExport] = useState(false);
  const { addErrorToast } = useStatusToasts();
  const { t } = useTranslation();

  const [showIssueModal] = useIssueModal({
    address: property?.streetAddress ?? "",
    coordinates,
    certificateId: elevationCertificate?.id,
  });

  const [showShareFileModal] = useShareFileModal();
  const objectId = property?.id ?? customMapGeometry?.id ?? null;
  const objectType =
    property?.id ? OBJECT_TYPE.PROPERTY
    : customMapGeometry?.id ? OBJECT_TYPE.CUSTOM_MAP_GEOMETRY
    : null;

  const [showDeleteDocumentUploadModal, hideDeleteDocumentUploadModal] =
    useModal(() => (
      <Modal onRequestClose={hideDeleteDocumentUploadModal}>
        <DeleteDocumentUploadConfirmation
          isDetailView={isDetailView}
          accountDocumentType={accountDocumentType}
          documentUploadId={documentUploadId}
          closeModal={hideDeleteDocumentUploadModal}
          onUpdate={onUpdate}
          objectId={objectId}
          objectType={objectType}
        />
      </Modal>
    ));

  const { account, authorized, isPublic } = useContext(AuthContext);

  const { submissionTypeId, hasMailingsSubmissionType } = useMemo(() => {
    if (!account?.submissionTypes)
      return { submissionTypeId: undefined, hasMailingsSubmissionType: false };

    const mailingsSubmissionTypes = account.submissionTypes.filter(
      submissionType =>
        submissionType.modules.includes(SUBMISSION_TYPE_MODULE.MAILINGS),
    );

    if (mailingsSubmissionTypes.length === 1) {
      return {
        submissionTypeId: mailingsSubmissionTypes[0]?.id,
        hasMailingsSubmissionType: true,
      };
    }

    return {
      submissionTypeId: undefined,
      hasMailingsSubmissionType: mailingsSubmissionTypes.length > 0,
    };
  }, [account?.submissionTypes]);

  const [showSendMailModal] = useSendMailModal({
    submissionTypeId,
    documentUploadId: documentUploadId,
    propertyId: property?.id ?? "",
    onSuccess: () => {
      onUpdate?.();
    },
  });

  const canShowReportDataIssue = authorized({
    resource: RESOURCE_NAME.ISSUE,
    permission: "create",
  });

  const canViewDocumentDetail = authorized({
    resource: RESOURCE_NAME.DOCUMENT_UPLOAD,
    permission: "view_details",
  });

  const canDeleteDocumentUpload = authorized({
    resource: RESOURCE_NAME.DOCUMENT_UPLOAD,
    permission: "delete",
  });

  const showAdminButton = authorized({
    resource: RESOURCE_NAME.CERTIFICATE,
    permission: "updateSettings",
  });

  const canShareDocumentUpload = authorized({
    resource: RESOURCE_NAME.DOCUMENT_UPLOAD,
    permission: "share",
  });

  const canSendMail = authorized({
    resource: RESOURCE_NAME.MAILING,
    permission: "create",
  });

  const viewLocation =
    canViewDocumentDetail ?
      {
        href: getDocumentUploadUrl({
          id: documentUpload.id,
          externalDocumentURL: documentUpload.externalDocumentURL ?? null,
          isPublic,
          intendedRoute:
            isPublic ? "guestDocumentUploadDetail" : "documentUploadDetail",
        }),
        target: "_blank",
      }
    : {
        href: getDocumentUploadUrl({
          id: documentUpload.id,
          externalDocumentURL: documentUpload.externalDocumentURL ?? null,
          isPublic,
          intendedRoute:
            isPublic ? "guestDocumentUploadFile" : "documentUploadFile",
        }),
        target: "_blank",
      };

  const adminSettingsAction: ActionsProps = {
    label: "Admin settings",
    to: {
      pathname: `/backoffice/certificates/${elevationCertificate?.id}`,
    },
    target: "_blank",
  };

  const deleteAction: ActionsProps = {
    label: "Delete",
    onClick: () => showDeleteDocumentUploadModal(),
    disabled: !canDeleteDocumentUpload,
    variant: "red",
  };

  const handleClickExportDocx = async () => {
    setLoadingDocxExport(true);

    track("Clicked 'Download .docx'", {
      documentType: accountDocumentType.name,
      propertyAddress: property?.streetAddress,
    });

    const failureToastText =
      "There was a problem with your download. Please try again or reach out to support@withforerunner.com for help.";

    try {
      const response = await httpClient(
        `/api/files/docx-export/${documentUploadId}`,
        {
          method: "GET",
        },
      );

      if (!response.ok) {
        addErrorToast(failureToastText);
        return;
      }

      const blobObject = await response.blob();
      const url = URL.createObjectURL(blobObject);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${originalFilename.split(".")[0]}.docx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      addErrorToast(failureToastText);
    }

    setLoadingDocxExport(false);
  };

  const exportDocxAction: ActionsProps = {
    label: "Download .docx",
    onClick: handleClickExportDocx,
  };

  let downloadAction: ActionsProps = {
    label: t("property-files-dropdown-download"),
    href: getDocumentUploadUrl({
      id: documentUpload.id,
      externalDocumentURL: documentUpload.externalDocumentURL ?? null,
      isPublic,
      intendedRoute: "documentUploadFile",
    }),
    download: true,
    target: "_blank",
    track: () => {
      track("File Downloaded", {
        documentUploadId,
        propertyId: property?.id,
        streetAddress: property?.streetAddress,
        documentTypeName: accountDocumentType.name,
      });
    },
  };

  const reportDataIssueAction: ActionsProps = {
    label: "Report data issue",
    onClick: () => showIssueModal(),
    disabled: !canShowReportDataIssue,
    closeOnClick: false,
  };

  const isInternalPageForPublicEC =
    elevationCertificate && !hiddenFromPublic && !isPublic;

  const shouldShowShareFileAction =
    isInternalPageForPublicEC &&
    account?.publicPortal.enabled &&
    canShareDocumentUpload;

  const showSendMailAction =
    canSendMail &&
    property?.id &&
    !isPublic &&
    hasMailingsSubmissionType &&
    mimeType === MIME_TYPE.PDF;

  const shareFileAction: ActionsProps = {
    label: "Share",
    disabled: !canShareDocumentUpload,
    onClick: () => {
      if (shouldShowShareFileAction) {
        track("Clicked 'Share'", { documentUploadId });
        showShareFileModal({
          ...documentUpload,
          elevationCertificate,
        });
      }
    },
  };

  const sendMailAction: ActionsProps = {
    label: "Send mail",
    onClick: () => {
      track("Clicked 'Send mail'", { documentUploadId });
      showSendMailModal();
    },
  };

  const actions: Array<ActionsProps> = [];
  if (!isDetailView) {
    actions.push({
      label: t("property-files-dropdown-view-file"),
      track: createTracker("View File clicked"),
      ...viewLocation,
    });

    if (
      certificateUpload?.status === CertificateUploadStatus.NEEDS_LOCATION &&
      !isPublic
    ) {
      actions.push({
        label: "Geocode",
        href: buildLink(
          "documentUploadDetail",
          {
            id: documentUpload.id,
          },
          { tab: DOCUMENT_UPLOAD_DETAIL_TAB_NAME.LOCATION },
        ),
        target: "_blank",
      });
    }
    if (documentUpload.submission?.id && !isPublic) {
      const viewRecordAction: ActionsProps = {
        label: "View related record",
        href: buildLink(
          "viewSubmission",
          {
            submissionId: documentUpload.submission.id,
          },
          {},
        ),
        target: "_blank",
      };
      actions.push(viewRecordAction);
    }

    if (!documentUpload.externalDocumentURL) {
      actions.push(downloadAction);
    }

    if (showSendMailAction) {
      actions.push(sendMailAction);
    }

    if (isDeletable && !isPublic) {
      actions.push(deleteAction);
    }
  } else {
    if (formData) {
      downloadAction.label = "Download .pdf";
      actions.push(exportDocxAction);
      actions.push(downloadAction);
    } else {
      actions.push(downloadAction);
    }

    if (shouldShowShareFileAction) {
      actions.push(shareFileAction);
    }

    if (documentUpload.submission?.id && !isPublic) {
      const viewRecordAction: ActionsProps = {
        label: "View related record",
        href: buildLink(
          "viewSubmission",
          {
            submissionId: documentUpload.submission.id,
          },
          {},
        ),
        target: "_blank",
      };
      actions.push(viewRecordAction);
    }

    if (isPublic && property) {
      actions.push({
        label: "View property",
        href: buildLink("residentProperty", {
          propertyId: property.id,
        }),
      });
    } else if (!isPublic && shouldShowViewOnMapActionForProperty(property)) {
      actions.push({
        label: "View on map",
        href: buildLink(
          "map",
          {},
          {
            address: property.streetAddress ?? "",
            propertyId: property.id,
            lat: property.latitude.toString(),
            lng: property.longitude.toString(),
          },
        ),
        target: "_blank",
      });
    } else if (
      !isPublic &&
      shouldShowViewOnMapActionForCustomMapGeometry(customMapGeometry)
    ) {
      actions.push({
        label: "View on map",
        href: buildLink(
          "map",
          { accountId: account?.id! },
          {
            geometryId: customMapGeometry.id,
            customMapId: customMapGeometry.customMapId,
          },
        ),
        target: "_blank",
      });
    }

    if (elevationCertificate) {
      showAdminButton && actions.push(adminSettingsAction);
      //always show the "Report data issue" option to internal users, but disable based on permissions
      //hide it for guests and public users
      !isPublic && actions.push(reportDataIssueAction);
    }

    if (showSendMailAction) {
      actions.push(sendMailAction);
    }

    if (isDeletable && !isPublic) {
      actions.push(deleteAction);
    }
  }

  const actionButton = ({ onClick }: { onClick: () => void }) => (
    <Button
      onPress={onClick}
      variant="outline"
      iconEnd="chevron-down"
      size="small"
      isPending={loadingDocxExport}
      data-testid="actions"
      title="Actions"
    />
  );

  return (
    <Actions
      buttonText={isDetailView ? "Actions" : undefined}
      disabled={disabled}
      actions={actions}
      customButton={isDetailView ? actionButton : undefined}
    />
  );
};

export default ActionButton;
